import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	type ColumnFiltersState,
	type ColumnOrderState,
	type ColumnSizingState,
	type ExpandedState,
	type GroupingState,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type Row,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import {
	Copy,
	Filter,
	GripVertical,
	type LucideIcon,
	Merge,
	Plus,
	RotateCcw,
	Send,
	Settings2,
	Trash2,
	X,
} from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
	useAuthCache,
	useEntryCalendarSummary,
	useMattersCache,
	useSettingsCache,
} from "@/shared/cache";
import { useEntryViewState } from "@/shared/contexts/EntryViewStateContext";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useScopedRowSelectionState } from "@/shared/hooks/useScopedRowSelectionState";
import { PROLAW_CUTOFF_LOCK_MESSAGE } from "@/shared/utils/exportedEntryMutation";
import { getEntriesDisplay } from "@/shared/utils/firm";
import {
	DatePicker,
	DatePickerDefaultFooter,
	type DateRange,
} from "@/v2/components/date-picker/DatePicker";
import { MatterLockButton } from "@/v2/components/MatterLockButton";
import { Button } from "@/v2/components/ui/button";
import { ButtonGroup } from "@/v2/components/ui/button-group";
import { Checkbox } from "@/v2/components/ui/checkbox";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { getAddEntryDefaultDate, isCompleteDateRange } from "@/v2/lib/dates";
import { cn } from "@/v2/lib/utils";
import { useEntryActions } from "./Entries/actions/useEntryActions";
import {
	COLUMN_LABELS,
	DEFAULT_COLUMN_ORDER,
	DEFAULT_COLUMN_VISIBILITY,
	type EntriesViewMeta,
	getEntriesColumns,
} from "./Entries/columns";
import { EntriesTable } from "./Entries/EntriesTable";
import { EntriesTimer } from "./Entries/EntriesTimer";
import {
	computeDateFilter,
	type DateFilter,
	makeEntryPredicate,
} from "./Entries/entryFilter";
import { entryFiltersToEntriesScope } from "./Entries/entryQueryScope";
import { isDefaultFilters } from "./Entries/filters/defaults";
import { FilterMenu } from "./Entries/filters/FilterMenu";
import type { EntryFilterState } from "./Entries/filters/types";
import { useFilterOptions } from "./Entries/filters/useFilterOptions";
import { useAttorneyFilters } from "./Entries/filters/useFilters";
import type { Entry } from "./Entries/normalize";
import { StatsBar, statsTotalsFromSummary } from "./Entries/StatsBar";
import { StatusIcon } from "./Entries/StatusIcon";
import { SearchBar } from "./Entries/search/SearchBar";
import { useFuseSearch } from "./Entries/search/useFuseSearch";
import {
	clearEntriesTableLayout,
	type EntriesTableColumnSizingBounds,
	type EntriesTableLayout,
	getEntriesTableLayoutStorageKey,
	readEntriesTableLayout,
	sanitizeEntriesTableColumnSizing,
	writeEntriesTableLayout,
} from "./Entries/tableLayoutStorage";
import { useConfiguredTimekeepers } from "./Entries/timekeepers";
import { useAllEntries } from "./Entries/useAllEntries";
import {
	columnOrderParser,
	DEFAULT_SORTING,
	sortingParser,
	visibilityParser,
} from "./Entries/viewParams";

function IconButton({
	icon: Icon,
	iconNode,
	label,
	onClick,
	disabled,
}: {
	icon?: LucideIcon;
	iconNode?: ReactNode;
	label: string;
	onClick?: () => void;
	disabled?: boolean;
}) {
	return (
		<TooltipButton
			variant="outline"
			size="icon"
			aria-label={label}
			onClick={onClick}
			disabled={disabled}
			className="group"
			tooltip={label}
		>
			{iconNode ?? (Icon ? <Icon /> : null)}
		</TooltipButton>
	);
}

function getSelectionScopeKey(
	dateFilter: DateFilter,
	filters: EntryFilterState,
	searchQuery: string,
): string {
	return [
		dateFilter.from ?? "",
		dateFilter.to ?? "",
		searchQuery.trim(),
		JSON.stringify(filters),
	].join("|");
}

const EMPTY_FILTER_OPTION_ENTRIES: readonly Entry[] = [];

function resolveUpdater<T>(updater: T | ((previous: T) => T), previous: T): T {
	return typeof updater === "function"
		? (updater as (value: T) => T)(previous)
		: updater;
}

function getColumnSizingBounds(
	columns: ReturnType<typeof getEntriesColumns>,
): EntriesTableColumnSizingBounds {
	const bounds: EntriesTableColumnSizingBounds = {};

	for (const column of columns) {
		const id = column.id;
		if (!id || column.enableResizing === false) continue;
		bounds[id] = {
			minSize: column.minSize,
			maxSize: column.maxSize,
		};
	}

	return bounds;
}

function ColumnsMenu({
	columnVisibility,
	onColumnVisibilityChange,
	columnOrder,
	onColumnOrderChange,
	hasCustomColumnSizing,
	onResetColumns,
	showTimekeeperColumn,
	autoHiddenColumns,
	defaultColumnVisibility,
}: {
	columnVisibility: VisibilityState;
	onColumnVisibilityChange: (next: VisibilityState) => void;
	columnOrder: ColumnOrderState;
	onColumnOrderChange: (next: ColumnOrderState) => void;
	hasCustomColumnSizing: boolean;
	onResetColumns: () => void;
	showTimekeeperColumn: boolean;
	/** Columns force-hidden by the current view; omitted from the menu so their toggles aren't dead. */
	autoHiddenColumns?: readonly string[];
	/** Firm-aware default visibility; drives the "customized" dot / reset affordance. */
	defaultColumnVisibility: VisibilityState;
}) {
	const hiddenKeys = useMemo(() => {
		const keys = new Set(autoHiddenColumns);
		if (!showTimekeeperColumn) keys.add("timekeeper");
		return keys;
	}, [autoHiddenColumns, showTimekeeperColumn]);
	const columnLabels = useMemo(
		() =>
			Object.fromEntries(
				Object.entries(COLUMN_LABELS).filter(([key]) => !hiddenKeys.has(key)),
			),
		[hiddenKeys],
	);
	const defaultColumnOrder = useMemo(
		() => DEFAULT_COLUMN_ORDER.filter((id) => !hiddenKeys.has(id)),
		[hiddenKeys],
	);
	const isVisible = (key: string) => columnVisibility[key] !== false;
	const isDefaultVisible = (key: string) =>
		defaultColumnVisibility[key] !== false;
	const orderedColumnIds = useMemo(() => {
		const allowed = new Set(defaultColumnOrder);
		const seen = new Set<string>();
		const out: string[] = [];
		for (const id of columnOrder) {
			if (!allowed.has(id) || seen.has(id)) continue;
			seen.add(id);
			out.push(id);
		}
		for (const id of defaultColumnOrder) {
			if (!seen.has(id)) out.push(id);
		}
		return out;
	}, [columnOrder, defaultColumnOrder]);
	const toggle = (key: string) =>
		onColumnVisibilityChange({ ...columnVisibility, [key]: !isVisible(key) });
	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		const oldIndex = orderedColumnIds.indexOf(String(active.id));
		const newIndex = orderedColumnIds.indexOf(String(over.id));
		if (oldIndex < 0 || newIndex < 0) return;
		onColumnOrderChange(arrayMove(orderedColumnIds, oldIndex, newIndex));
	};
	const hasCustomColumns =
		Object.keys(columnLabels).some(
			(key) => isVisible(key) !== isDefaultVisible(key),
		) ||
		orderedColumnIds.join(",") !== defaultColumnOrder.join(",") ||
		hasCustomColumnSizing;

	return (
		<Popover>
			<Tooltip>
				<TooltipTrigger asChild>
					<PopoverTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							aria-label="Columns"
							className="relative size-7 bg-muted text-muted-foreground hover:bg-muted hover:text-foreground"
						>
							<Settings2 className="size-4" />
							{hasCustomColumns && (
								<span className="absolute top-1 right-1 size-1.5 rounded-full bg-ring" />
							)}
						</Button>
					</PopoverTrigger>
				</TooltipTrigger>
				<TooltipContent side="bottom">Columns</TooltipContent>
			</Tooltip>
			<PopoverContent align="end" className="w-56 p-2">
				<div className="px-2 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
					Columns
				</div>
				<DndContext
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={orderedColumnIds}
						strategy={verticalListSortingStrategy}
					>
						<div className="flex flex-col">
							{orderedColumnIds.map((key) => (
								<ColumnMenuRow
									key={key}
									id={key}
									label={columnLabels[key] ?? key}
									checked={isVisible(key)}
									onToggle={() => toggle(key)}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
				{hasCustomColumns && (
					<div className="mt-1 border-t border-border pt-1">
						<Button
							variant="ghost"
							size="sm"
							onClick={onResetColumns}
							className="h-7 w-full text-xs text-muted-foreground hover:text-foreground"
						>
							Reset to default
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

function ColumnMenuRow({
	id,
	label,
	checked,
	onToggle,
}: {
	id: string;
	label: string;
	checked: boolean;
	onToggle: () => void;
}) {
	const {
		attributes,
		listeners,
		setActivatorNodeRef,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
			}}
			className={cn("flex items-center rounded-sm", isDragging && "z-10")}
		>
			<Button
				variant="ghost"
				size="sm"
				onClick={onToggle}
				className="h-8 min-w-0 flex-1 justify-start gap-2 rounded-sm px-2 text-xs font-normal hover:bg-accent hover:text-accent-foreground"
			>
				<Checkbox
					checked={checked}
					tabIndex={-1}
					aria-hidden
					className="pointer-events-none shrink-0"
				/>
				<span className="truncate">{label}</span>
			</Button>
			<button
				type="button"
				ref={setActivatorNodeRef}
				aria-label={`Reorder ${label} column`}
				className="flex h-8 w-7 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground active:cursor-grabbing"
				{...attributes}
				{...listeners}
			>
				<GripVertical className="size-3.5" />
			</button>
		</div>
	);
}

interface ToolbarProps {
	date: Date;
	dateRange: Partial<DateRange>;
	onDateChange: (d: Date) => void;
	onDateRangeChange: (r: DateRange) => void;
	filterSlot: React.ReactNode;
	matterLockSlot: ReactNode;
	searchSlot: ReactNode;
	timerSlot: ReactNode;
	selectedCount: number;
	/**
	 * Exported rows are terminal, and deleted rows must be restored before
	 * normal mutations. Duplicate stays clickable because v2 duplicate produces
	 * a fresh tracked copy from any state.
	 */
	selectionHasExported: boolean;
	selectionHasLockedExported: boolean;
	selectionHasProLawCutoffLocked: boolean;
	selectionHasDeleted: boolean;
	selectedDeletedCount: number;
	onClearSelection: () => void;
	onAdd: () => void;
	onExport: () => void;
	onRestoreDeleted: () => void;
	onDelete: () => void;
	onDuplicate: () => void;
	onCombine: () => void;
	onMarkReviewed: () => void;
	onMarkTracked: () => void;
}

const TOOLBAR_TWO_ROW_BREAKPOINT_PX = 960;
const TOOLBAR_SELECTED_TWO_ROW_BREAKPOINT_PX = 1120;

function ToolbarControlCluster({
	date,
	dateRange,
	onDateChange,
	onDateRangeChange,
	filterSlot,
	searchSlot,
}: {
	date: Date;
	dateRange: Partial<DateRange>;
	onDateChange: (d: Date) => void;
	onDateRangeChange: (r: DateRange) => void;
	filterSlot: ReactNode;
	searchSlot: ReactNode;
}) {
	const selectedDateRange = isCompleteDateRange(dateRange)
		? dateRange
		: { from: date, to: date };

	return (
		<>
			{filterSlot}
			<DatePicker
				className="w-fit"
				dateRange={selectedDateRange}
				onDateChange={onDateChange}
				onDateRangeChange={onDateRangeChange}
				selectionMode="range"
				bottomSlot={DatePickerDefaultFooter}
			/>
			{searchSlot}
		</>
	);
}

function ToolbarControlsRow({
	left,
	right,
}: {
	left: ReactNode;
	right: ReactNode;
}) {
	return (
		<div className="mb-2 flex min-h-10 flex-wrap items-center gap-2 animate-in fade-in-0 slide-in-from-top-1 duration-150">
			<div className="flex min-w-0 items-center gap-2">{left}</div>
			<div className="ml-auto flex shrink-0 items-center gap-2">{right}</div>
		</div>
	);
}

function Toolbar({
	date,
	dateRange,
	onDateChange,
	onDateRangeChange,
	filterSlot,
	matterLockSlot,
	searchSlot,
	timerSlot,
	selectedCount,
	selectionHasExported,
	selectionHasLockedExported,
	selectionHasProLawCutoffLocked,
	selectionHasDeleted,
	selectedDeletedCount,
	onClearSelection,
	onAdd,
	onExport,
	onRestoreDeleted,
	onDelete,
	onDuplicate,
	onCombine,
	onMarkReviewed,
	onMarkTracked,
}: ToolbarProps) {
	const hasSelection = selectedCount >= 1;
	const lockedByStatus = selectionHasExported || selectionHasDeleted;
	const deleteLocked = selectionHasDeleted || selectionHasLockedExported;
	const statusLockReason = selectionHasDeleted ? "deleted" : "exported";
	const primaryIsRestore = selectionHasDeleted;
	const primaryAction = primaryIsRestore
		? {
				key: "restore",
				label: "Restore",
				Icon: RotateCcw,
				onClick: onRestoreDeleted,
				disabled: selectedDeletedCount === 0,
				className:
					"border border-primary/30 bg-primary/10 text-primary shadow-primary/10 hover:bg-primary/15",
			}
		: hasSelection
			? {
					key: "export",
					label: "Export",
					Icon: Send,
					onClick: onExport,
					disabled: selectionHasExported,
					className:
						"bg-success text-success-foreground shadow-success/25 hover:bg-success/90",
				}
			: {
					key: "export",
					label: "Export",
					Icon: Send,
					onClick: onExport,
					disabled: true,
					className:
						"bg-success text-success-foreground shadow-success/25 hover:bg-success/90",
				};
	const toolbarRef = useRef<HTMLDivElement>(null);
	const frameRef = useRef(0);
	const [centerIsRaised, setCenterIsRaised] = useState(false);
	const twoRowBreakpoint = hasSelection
		? TOOLBAR_SELECTED_TWO_ROW_BREAKPOINT_PX
		: TOOLBAR_TWO_ROW_BREAKPOINT_PX;
	const measureToolbar = useCallback(() => {
		const toolbar = toolbarRef.current;
		if (!toolbar) return;

		cancelAnimationFrame(frameRef.current);
		frameRef.current = requestAnimationFrame(() => {
			setCenterIsRaised(toolbar.clientWidth < twoRowBreakpoint);
		});
	}, [twoRowBreakpoint]);

	useEffect(() => {
		const toolbar = toolbarRef.current;
		if (!toolbar) return;

		measureToolbar();

		if (typeof ResizeObserver === "undefined") {
			window.addEventListener("resize", measureToolbar);
			return () => {
				window.removeEventListener("resize", measureToolbar);
				cancelAnimationFrame(frameRef.current);
			};
		}

		const observer = new ResizeObserver(measureToolbar);
		observer.observe(toolbar);
		window.addEventListener("resize", measureToolbar);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measureToolbar);
			cancelAnimationFrame(frameRef.current);
		};
	}, [measureToolbar]);

	return (
		<TooltipProvider delayDuration={300}>
			<div className="bg-background px-4 pt-4 pb-2">
				{centerIsRaised && (
					<ToolbarControlsRow
						left={
							<ToolbarControlCluster
								date={date}
								dateRange={dateRange}
								onDateChange={onDateChange}
								onDateRangeChange={onDateRangeChange}
								filterSlot={filterSlot}
								searchSlot={searchSlot}
							/>
						}
						right={null}
					/>
				)}

				<div
					ref={toolbarRef}
					className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2"
				>
					{/* Left: bulk-action buttons. Always visible; disabled until the
				    selection is large enough (and not locked by an exported row). */}
					<div className="col-start-1 flex min-w-max items-center gap-2 justify-self-start">
						<ButtonGroup>
							<IconButton
								icon={Copy}
								label="Duplicate"
								onClick={onDuplicate}
								disabled={!hasSelection}
							/>
							<IconButton
								icon={Merge}
								label={
									lockedByStatus
										? `Combine (selection includes ${statusLockReason})`
										: "Combine"
								}
								onClick={onCombine}
								disabled={selectedCount < 2 || lockedByStatus}
							/>
						</ButtonGroup>

						<ButtonGroup>
							<IconButton
								iconNode={<StatusIcon status="tracked" />}
								label={
									lockedByStatus
										? `Mark tracked (selection includes ${statusLockReason})`
										: "Mark tracked"
								}
								onClick={onMarkTracked}
								disabled={!hasSelection || lockedByStatus}
							/>
							<IconButton
								iconNode={<StatusIcon status="reviewed" />}
								label={
									lockedByStatus
										? `Mark reviewed (selection includes ${statusLockReason})`
										: "Mark reviewed"
								}
								onClick={onMarkReviewed}
								disabled={!hasSelection || lockedByStatus}
							/>
							<IconButton
								iconNode={<Trash2 className="text-destructive" />}
								label={
									selectionHasProLawCutoffLocked
										? PROLAW_CUTOFF_LOCK_MESSAGE
										: deleteLocked
										? `Delete (selection includes ${statusLockReason})`
										: "Delete"
								}
								onClick={onDelete}
								disabled={!hasSelection || deleteLocked}
							/>
						</ButtonGroup>

						{hasSelection && (
							<div className="flex items-center gap-0.5 pl-1 animate-in fade-in-0 slide-in-from-left-2 duration-150">
								<span className="text-sm font-medium tabular-nums">
									{selectedCount} selected
								</span>
								<Button
									variant="ghost"
									size="icon"
									className="size-7 text-muted-foreground"
									onClick={onClearSelection}
									aria-label="Clear selection"
								>
									<X />
								</Button>
							</div>
						)}
					</div>

					{/* Center: filters/search, calendar, timer context. */}
					{!centerIsRaised && (
						<div className="col-start-2 flex shrink-0 items-center gap-2 justify-self-center">
							<ToolbarControlCluster
								date={date}
								dateRange={dateRange}
								onDateChange={onDateChange}
								onDateRangeChange={onDateRangeChange}
								filterSlot={filterSlot}
								searchSlot={searchSlot}
							/>
						</div>
					)}

					{/* Right: matter/timer context beside the primary action. */}
					<div className="col-start-3 flex min-w-max items-center justify-end gap-2 justify-self-end">
						{timerSlot}
						{matterLockSlot}
						<Button variant="outline" aria-label="Add" onClick={onAdd}>
							<Plus />
							Add
						</Button>
						<Button
							variant="default"
							aria-label={primaryAction.label}
							onClick={primaryAction.onClick}
							disabled={primaryAction.disabled}
							className={cn("min-w-28", primaryAction.className)}
						>
							<span className="relative z-10 inline-flex items-center gap-2">
								<span
									key={`${primaryAction.key}-icon`}
									className="inline-flex animate-in fade-in-0 zoom-in-75 spin-in-12 duration-200"
								>
									<primaryAction.Icon />
								</span>
								<span
									key={`${primaryAction.key}-label`}
									className="inline-block animate-in fade-in-0 slide-in-from-right-1 duration-200"
								>
									{primaryAction.label}
								</span>
							</span>
						</Button>
					</div>
				</div>
			</div>
		</TooltipProvider>
	);
}

export default function EntriesView() {
	// Search, sorting and columns remain URL-backed. Entry filters and dates live
	// in EntryViewStateProvider so Attorney/Admin surfaces share them in memory.
	const [searchParams] = useSearchParams();
	const hasColumnVisibilityParam = searchParams.has("cols");
	const hasColumnOrderParam = searchParams.has("colOrder");
	const [sorting, setSorting] = useQueryState(
		"sort",
		sortingParser
			.withDefault(DEFAULT_SORTING)
			.withOptions({ history: "replace" }),
	);
	const [urlColumnVisibility, setUrlColumnVisibility] = useQueryState(
		"cols",
		visibilityParser
			.withDefault(DEFAULT_COLUMN_VISIBILITY)
			.withOptions({ history: "replace" }),
	);
	const [urlColumnOrder, setUrlColumnOrder] = useQueryState(
		"colOrder",
		columnOrderParser
			.withDefault(DEFAULT_COLUMN_ORDER)
			.withOptions({ history: "replace" }),
	);
	const [savedColumnLayout, setSavedColumnLayout] =
		useState<EntriesTableLayout | null>(null);
	const [expanded, setExpanded] = useState<ExpandedState>(true);

	// The search box stays controlled by local state for snappy typing; only
	// the debounced value is mirrored into the `q` param (and used for Fuse).
	const [qParam, setQParam] = useQueryState(
		"q",
		parseAsString.withDefault("").withOptions({ history: "replace" }),
	);
	const [searchQuery, setSearchQuery] = useState(qParam);
	const debouncedQuery = useDebounce(searchQuery, 150);
	useEffect(() => {
		setQParam(debouncedQuery === "" ? null : debouncedQuery);
	}, [debouncedQuery, setQParam]);

	const { date, dateRange, setDate, setDateRange } = useEntryViewState();

	const timekeepers = useConfiguredTimekeepers();
	const showTimekeeperColumn = timekeepers !== null;
	const fieldOptions = useFilterOptions(
		EMPTY_FILTER_OPTION_ENTRIES,
		timekeepers,
	);
	const {
		filters,
		defaultFilters,
		toggleFieldValue: toggleFilterFieldValue,
		selectAll: selectAllFilterValues,
		selectNone: selectNoFilterValues,
		reset: resetFilters,
	} = useAttorneyFilters(fieldOptions);
	const effectiveFilters = filters;
	const dateFilter = useMemo(
		() => computeDateFilter(date, dateRange),
		[date, dateRange],
	);
	const entriesScope = useMemo(
		() =>
			entryFiltersToEntriesScope(effectiveFilters, dateFilter, {
				includeAttachments: false,
			}),
		[effectiveFilters, dateFilter],
	);
	const { data: entries, isLoading } = useAllEntries(entriesScope);
	const data = entries ?? [];
	const statsScope = useMemo(
		() =>
			entryFiltersToEntriesScope(effectiveFilters, dateFilter, {
				statuses: ["tracked", "reviewed", "exported"],
				includeFieldFilters: false,
				includeAttachments: false,
			}),
		[effectiveFilters, dateFilter],
	);
	const { data: statsDays } = useEntryCalendarSummary(statsScope);
	const statsTotals = useMemo(
		() => statsTotalsFromSummary(statsDays),
		[statsDays],
	);

	const scopedData = data;
	const searchHits = useFuseSearch(scopedData, debouncedQuery);

	const { matters } = useMattersCache();
	const { settings, updateSettings } = useSettingsCache();
	const { auth } = useAuthCache();

	const selectionScopeKey = useMemo(
		() => getSelectionScopeKey(dateFilter, effectiveFilters, debouncedQuery),
		[dateFilter, effectiveFilters, debouncedQuery],
	);
	const { rowSelection, handleRowSelectionChange, clearSelection } =
		useScopedRowSelectionState(selectionScopeKey, {
			restoreOnScopeReturn: false,
		});

	// TanStack keys columnFilters by id. Missing non-status fields are implicit all;
	// explicit empty arrays intentionally match nothing.
	const columnFilters = useMemo<ColumnFiltersState>(() => {
		const out: ColumnFiltersState = [{ id: "date", value: dateFilter }];
		out.push({ id: "status", value: { values: filters.status } });
		if (filters.timekeeper !== undefined) {
			out.push({
				id: "timekeeper",
				value: { values: filters.timekeeper },
			});
		}
		for (const field of [
			"application",
			"matter",
			"client",
			"billableCategory",
		] as const) {
			const values = filters[field];
			if (values !== undefined) out.push({ id: field, value: { values } });
		}
		return out;
	}, [dateFilter, filters]);

	// Derive selected entries directly from rowSelection + data instead of
	// `table.getSelectedRowModel()` — the table's computed model is not
	// reference-stable, which would re-trigger the action hook's memos on every
	// table render. Scope the derivation to the current date/filter/search view so
	// hidden rows cannot remain selected and be affected by toolbar actions.
	// Exported rows ARE selectable (so they participate in the per-row gestures,
	// e.g. duplicate-many), but the toolbar handlers in `useEntryActions` decide
	// per-action whether to skip them.
	const selectedEntries = useMemo(() => {
		const predicate = makeEntryPredicate(effectiveFilters, dateFilter);
		return scopedData.filter(
			(e) =>
				rowSelection[e.id] &&
				predicate(e) &&
				(searchHits === null || searchHits.has(e.id)),
		);
	}, [scopedData, dateFilter, effectiveFilters, rowSelection, searchHits]);
	const selectedCount = selectedEntries.length;
	const tableColumns = useMemo(
		() => getEntriesColumns(showTimekeeperColumn ? timekeepers : null),
		[showTimekeeperColumn, timekeepers],
	);
	const columnSizingBounds = useMemo(
		() => getColumnSizingBounds(tableColumns),
		[tableColumns],
	);
	const layoutStorageKey = useMemo(
		() => getEntriesTableLayoutStorageKey(auth.email),
		[auth.email],
	);
	useEffect(() => {
		setSavedColumnLayout(
			readEntriesTableLayout(layoutStorageKey, columnSizingBounds),
		);
	}, [layoutStorageKey, columnSizingBounds]);

	// Firm-level entries defaults (e.g. Taylor Day / test start Billing hidden).
	// Hostname-derived, so stable for the session. `defaultHiddenColumns` folds
	// onto the shared default so those columns start hidden but stay toggleable
	// in the Columns menu and reset back to hidden.
	const entriesDisplay = useMemo(() => getEntriesDisplay(), []);
	const firmDefaultColumnVisibility = useMemo<VisibilityState>(() => {
		if (entriesDisplay.defaultHiddenColumns.length === 0)
			return DEFAULT_COLUMN_VISIBILITY;
		const next: VisibilityState = { ...DEFAULT_COLUMN_VISIBILITY };
		for (const id of entriesDisplay.defaultHiddenColumns) next[id] = false;
		return next;
	}, [entriesDisplay.defaultHiddenColumns]);

	const columnVisibility = useMemo(
		() =>
			hasColumnVisibilityParam
				? urlColumnVisibility
				: (savedColumnLayout?.columnVisibility ?? firmDefaultColumnVisibility),
		[
			hasColumnVisibilityParam,
			savedColumnLayout,
			urlColumnVisibility,
			firmDefaultColumnVisibility,
		],
	);
	const columnOrder = useMemo(
		() =>
			hasColumnOrderParam
				? urlColumnOrder
				: (savedColumnLayout?.columnOrder ?? DEFAULT_COLUMN_ORDER),
		[hasColumnOrderParam, savedColumnLayout, urlColumnOrder],
	);
	const columnSizing = savedColumnLayout?.columnSizing ?? {};
	const persistColumnLayout = useCallback(
		(updates: Partial<Omit<EntriesTableLayout, "version">>) => {
			const nextLayout = writeEntriesTableLayout(
				layoutStorageKey,
				{
					version: 1,
					columnVisibility: updates.columnVisibility ?? columnVisibility,
					columnOrder: updates.columnOrder ?? columnOrder,
					columnSizing: updates.columnSizing ?? columnSizing,
				},
				columnSizingBounds,
			);
			setSavedColumnLayout(nextLayout);
		},
		[
			columnOrder,
			columnSizing,
			columnSizingBounds,
			columnVisibility,
			layoutStorageKey,
		],
	);
	const handleColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>(
		(updater) => {
			const next = resolveUpdater(updater, columnVisibility);
			setUrlColumnVisibility(next);
			persistColumnLayout({ columnVisibility: next });
		},
		[columnVisibility, persistColumnLayout, setUrlColumnVisibility],
	);
	const handleColumnOrderChange = useCallback<OnChangeFn<ColumnOrderState>>(
		(updater) => {
			const next = resolveUpdater(updater, columnOrder);
			setUrlColumnOrder(next);
			persistColumnLayout({ columnOrder: next });
		},
		[columnOrder, persistColumnLayout, setUrlColumnOrder],
	);
	const handleColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>(
		(updater) => {
			const next = sanitizeEntriesTableColumnSizing(
				resolveUpdater(updater, columnSizing),
				columnSizingBounds,
			);
			persistColumnLayout({ columnSizing: next });
		},
		[columnSizing, columnSizingBounds, persistColumnLayout],
	);
	const handleResetColumnLayout = useCallback(() => {
		setUrlColumnVisibility(firmDefaultColumnVisibility);
		setUrlColumnOrder(DEFAULT_COLUMN_ORDER);
		clearEntriesTableLayout(layoutStorageKey);
		setSavedColumnLayout(null);
	}, [
		firmDefaultColumnVisibility,
		layoutStorageKey,
		setUrlColumnOrder,
		setUrlColumnVisibility,
	]);
	const hasCustomColumnSizing = Object.keys(columnSizing).length > 0;

	// When the table shows a single timekeeper's entries — no selection (the
	// server scopes to the logged-in user) or exactly one selected — the
	// Timekeeper and Billing columns are redundant, so hide them. This is a
	// display-time override on top of the persisted visibility state; both
	// columns return as soon as multiple timekeepers are selected.
	const singleTimekeeperView =
		showTimekeeperColumn &&
		(filters.timekeeper === undefined || filters.timekeeper.length === 1);
	const autoHiddenColumns = useMemo(
		() => (singleTimekeeperView ? ["timekeeper", "billableCategory"] : []),
		[singleTimekeeperView],
	);
	const effectiveColumnVisibility = useMemo(() => {
		const next: VisibilityState = { ...columnVisibility };
		for (const key of Object.keys(DEFAULT_COLUMN_VISIBILITY)) next[key] = false;
		for (const key of autoHiddenColumns) next[key] = false;
		return next;
	}, [columnVisibility, autoHiddenColumns]);
	useEffect(() => {
		if (!sorting.some((s) => s.id === "timekeeper")) return;
		const next = sorting.filter((s) => s.id !== "timekeeper");
		setSorting(next.length > 0 ? next : DEFAULT_SORTING);
	}, [sorting, setSorting]);
	const visibleColumnOrder = useMemo(
		() =>
			showTimekeeperColumn
				? columnOrder
				: columnOrder.filter((id) => id !== "timekeeper"),
		[columnOrder, showTimekeeperColumn],
	);
	const tableColumnOrder = useMemo(
		() => [
			"_select",
			...visibleColumnOrder,
			"client",
			"task",
			"activity",
			"matterName",
			"billable",
			"unitsSeconds",
		],
		[visibleColumnOrder],
	);
	const actions = useEntryActions({
		selectedEntries,
		clearSelection,
		addDefaultDate: getAddEntryDefaultDate(date, dateRange),
		timekeeperId:
			filters.timekeeper?.length === 1
				? filters.timekeeper[0]
				: defaultFilters.timekeeper?.[0],
		timekeepers,
	});

	// Grouping follows the active sort column; defaults to date when unsorted.
	const effectiveSorting = useMemo(
		() => sorting.filter((s) => s.id !== "timekeeper"),
		[sorting],
	);
	const sortBy = effectiveSorting[0]?.id ?? "date";
	const grouping = useMemo<GroupingState>(() => [sortBy], [sortBy]);
	const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
		(updater) => {
			// Group row ids are derived from the active grouping/sort column. If the
			// user previously collapsed one group, `expanded` becomes a sparse object;
			// after a sort/group change that object does not describe the new grouped
			// row model, so the table appears fully collapsed. Match the mobile list's
			// default behavior by reopening all groups whenever sorting changes.
			setExpanded(true);
			setSorting(updater);
		},
		[setSorting],
	);

	const table = useReactTable({
		data: scopedData,
		columns: tableColumns,
		state: {
			sorting: effectiveSorting,
			grouping,
			expanded,
			columnVisibility: effectiveColumnVisibility,
			columnOrder: tableColumnOrder,
			rowSelection,
			columnFilters,
			columnSizing,
			globalFilter: searchHits,
		},
		onSortingChange: handleSortingChange,
		onExpandedChange: setExpanded,
		onColumnVisibilityChange: handleColumnVisibilityChange,
		onColumnOrderChange: handleColumnOrderChange,
		onRowSelectionChange: handleRowSelectionChange,
		onColumnSizingChange: handleColumnSizingChange,
		enableRowSelection: (row) => !row.getIsGrouped(),
		enableGrouping: true,
		autoResetExpanded: false,
		enableColumnResizing: true,
		groupedColumnMode: false,
		// `onEnd` commits the new size once at mouseup instead of on every
		// mousemove. During drag the body grid stays put — only the resize
		// handle slides via `translateX(deltaOffset)` (see DataTableResizeHandle).
		// Avoids per-frame grid relayout of every row.
		columnResizeMode: "onEnd",
		defaultColumn: { minSize: 40, maxSize: 800 },
		getRowId: (row) => row.id,
		getCoreRowModel: getCoreRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		globalFilterFn: (row: Row<Entry>, _id, value) =>
			value == null || (value as Set<string>).has(row.original.id),
		meta: {
			rowActions: actions.rowActions,
		} satisfies EntriesViewMeta,
	});

	const filterMenuSharedProps = {
		fieldOptions,
		filters: effectiveFilters,
		defaultFilters,
		onToggleFieldValue: toggleFilterFieldValue,
		onSelectAll: selectAllFilterValues,
		onSelectNone: selectNoFilterValues,
		onReset: resetFilters,
	};

	const hasActiveFilters = !isDefaultFilters(effectiveFilters, defaultFilters);

	return (
		<div className="w-full h-full flex flex-col">
			<Toolbar
				date={date}
				dateRange={dateRange}
				onDateChange={setDate}
				onDateRangeChange={setDateRange}
				filterSlot={
					<FilterMenu
						{...filterMenuSharedProps}
						trigger={
							<Button
								variant="outline"
								size="icon"
								aria-label="Filter"
								className={cn(
									"relative",
									hasActiveFilters &&
										"border-ring text-ring hover:border-ring hover:text-ring",
								)}
							>
								<Filter />
								{hasActiveFilters && (
									<span className="absolute right-1 top-1 size-1.5 rounded-full bg-ring" />
								)}
							</Button>
						}
					/>
				}
				matterLockSlot={
					<MatterLockButton
						matters={matters}
						value={settings.hardcoded_matter}
						onChange={(id) => updateSettings({ hardcoded_matter: id })}
					/>
				}
				searchSlot={
					<SearchBar
						value={searchQuery}
						onChange={setSearchQuery}
						onClear={() => setSearchQuery("")}
					/>
				}
				timerSlot={<EntriesTimer onAddEntry={actions.onAddWithDuration} />}
				selectedCount={selectedCount}
				selectionHasExported={actions.selectionHasExported}
				selectionHasLockedExported={actions.selectionHasLockedExported}
				selectionHasProLawCutoffLocked={
					actions.selectionHasProLawCutoffLocked
				}
				selectionHasDeleted={actions.selectionHasDeleted}
				selectedDeletedCount={actions.selectedDeletedCount}
				onClearSelection={clearSelection}
				onAdd={actions.onAdd}
				onExport={actions.onExport}
				onRestoreDeleted={actions.onRestoreDeleted}
				onDelete={actions.onDelete}
				onDuplicate={actions.onDuplicate}
				onCombine={actions.onCombine}
				onMarkReviewed={actions.onMarkReviewed}
				onMarkTracked={actions.onMarkTracked}
			/>
			<div className="flex-1 min-h-0 px-4 pb-4 pt-1">
				<div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-background">
					<TooltipProvider delayDuration={300}>
						<div className="relative flex-1 min-h-0">
							<EntriesTable
								table={table}
								isLoading={isLoading}
								rowSelection={rowSelection}
								onRowClick={actions.onRowClick}
								exportedRowsInert={false}
								timekeepers={timekeepers}
								columnsMenu={
									<ColumnsMenu
										columnVisibility={columnVisibility}
										onColumnVisibilityChange={handleColumnVisibilityChange}
										columnOrder={columnOrder}
										onColumnOrderChange={handleColumnOrderChange}
										hasCustomColumnSizing={hasCustomColumnSizing}
										onResetColumns={handleResetColumnLayout}
										showTimekeeperColumn={showTimekeeperColumn}
										autoHiddenColumns={autoHiddenColumns}
										defaultColumnVisibility={firmDefaultColumnVisibility}
									/>
								}
							/>
						</div>
					</TooltipProvider>

					<StatsBar totals={statsTotals} />
				</div>
			</div>

			{actions.dialogs}
		</div>
	);
}
